import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter";
import type { AdminDashboardAppliedFilters } from "../types/consultationsBilling.types";

const DEFAULT_DEBOUNCE_MS = 500;

export function useAdminDashboardFilters(debounceMs = DEFAULT_DEBOUNCE_MS) {
  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  const [appliedFilters, setAppliedFilters] = useState<AdminDashboardAppliedFilters>({
    dateRange,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters({ dateRange });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [dateRange, debounceMs]);

  const resetAll = () => {
    handleDateChange(dayjs());
    handleFilterTypeChange("month");
  };

  const isDefaultPeriod = useMemo(
    () =>
      dateFilterType === "month" && selectedDate.isSame(dayjs(), "month"),
    [dateFilterType, selectedDate]
  );

  const hasActiveFilters = useMemo(() => !isDefaultPeriod, [isDefaultPeriod]);

  const resetKey = useMemo(
    () =>
      `${appliedFilters.dateRange.startDate}-${appliedFilters.dateRange.endDate}`,
    [appliedFilters]
  );

  return {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
    resetAll,
    hasActiveFilters,
    isDefaultPeriod,
    appliedFilters,
    resetKey,
  };
}
